import React, { useEffect } from 'react'
import {
  Typography,
  Box,
} from '@material-ui/core'
import classNames from 'classnames'
import {
  Day as DayType, Meridiem,
} from '../Calendar'
import WeeklyCalendarDay from './WeeklyCalendarDay'
import classes from './WeeklyCalendar.styles'

interface WeeklyDesktopCalendarProps {
    readonly selectedTimeIndex?: number;
    readonly selectedMeridiem?: Meridiem;
    readonly selectedDayIndex: number;
    readonly selectedWeek?: number;
    readonly currentWeek?: number;
    readonly days: DayType[];
    changeTimeslot({
      dayIndex, meridiem, timeIndex, onMobile,
    }: {
      dayIndex: number;
      meridiem?: Meridiem;
      timeIndex?: number;
      onMobile?: boolean;
  }): Promise<void>;
}

const WeeklyDesktopCalendar = ({
  days,
  selectedTimeIndex,
  selectedDayIndex,
  selectedMeridiem,
  changeTimeslot,
  selectedWeek,
  currentWeek,
}: WeeklyDesktopCalendarProps) => {
  const currentDate = new Date()
  const currentDay = currentDate.getDate().toString()
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ]
  const currentMonth = months[currentDate.getMonth()]

  const [selectedDayIndexState, setSelectedDayIndexState] = React.useState(selectedDayIndex)

  useEffect(() => {
    // If there's no selected timeslot , find first available day
    if (!selectedWeek || selectedWeek !== currentWeek) {
      const firstAvailableIndex = days.findIndex(day => 
        Object.keys(day.timeslots).some((meridiem: Meridiem) =>
          day.timeslots[meridiem].some(timeslot => 
            timeslot.timeslot?.available
          )
        )
      )
      setSelectedDayIndexState(firstAvailableIndex >= 0 ? firstAvailableIndex : 0)
    } else {
      // If there's a selected timeslot, use that day's index
      setSelectedDayIndexState(selectedDayIndex)
    }
  }, [currentWeek, selectedDayIndex, selectedWeek, days])

  return (
    <Box 
      display="flex" 
      gridGap={0} 
      width="100%"
      justifyContent="center"
      flexDirection="row"
      alignItems="center"
      flexWrap="nowrap"
      mt={8.4}
      mb={22}
      className={classes.weekDaysWrapper}
    >
      {days.map((day, i) => {
        const highlightedDay = day.date === currentDay && day.month === currentMonth
  
         // Check if both AM and PM slots are fully booked
        const isFullyBooked = Object.keys(day.timeslots).every((meridiem: Meridiem) => 
          day.timeslots[meridiem].every(timeslot => 
            !timeslot.timeslot?.available && !day.hasSelectedTimeslot
          )
        )

        return (
          <Box 
            key={day.date}
            id={`day-${day?.month}${day?.date}`}
          >
            <Typography variant="body1" align="center">{day.dayOfWeek}</Typography>
            <button
              key={day.date}
              className={classNames(
                classes.weekDay, 
                {
                  preselected: i === selectedDayIndexState,
                  selected: day.hasSelectedTimeslot,
                  first: i === 0,
                  last: i === days.length - 1,
                  currentDate: highlightedDay,
                  disabled: isFullyBooked,
                }
              )}
              onClick={(e) => {
                e.preventDefault()
                setSelectedDayIndexState(i)
              }}
            >
              <Typography 
                variant="body2"
                align="center"
              >
                {day.date}
              </Typography>
            </button>
            {selectedDayIndexState === i && (
              <WeeklyCalendarDay
                selectedTimeIndex={selectedTimeIndex}
                selectedDayIndex={selectedDayIndexState}
                selectedMeridiem={selectedMeridiem}
                selectedWeek={selectedWeek}
                currentWeek={currentWeek}
                currentDayIndex={i}
                changeTimeslot={changeTimeslot}
                day={day}
              />
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default WeeklyDesktopCalendar
